import React, { useState, useEffect } from "react";
import { Modal, Form, InputNumber, Select, Radio, Button, DatePicker, Checkbox, Row, Col } from "antd";
import { getGameTypes } from "../../api/services/gameTypeService";
import { getPlayers } from "../../api/services/playerService";
import dayjs from "dayjs";

const EditMatchModal = ({ visible, onClose, onUpdateMatch, match }) => {
  const [form] = Form.useForm();
  const [gameTypes, setGameTypes] = useState([]);
  const [players, setPlayers] = useState([]);
  const [raceType, setRaceType] = useState("race_to");
  const [isOpenEnd, setIsOpenEnd] = useState(false);
  const [isPractice, setIsPractice] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedGameTypes = await getGameTypes();
      const fetchedPlayers = await getPlayers();
      setGameTypes(fetchedGameTypes);
      setPlayers(fetchedPlayers);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (match && visible) {
      // Setze Formularwerte basierend auf Match-Daten
      const hasRaceTo = match.race_to && match.race_to > 0;
      const hasBestOf = match.best_of && match.best_of > 0;

      setRaceType(hasRaceTo ? "race_to" : "best_of");
      setIsOpenEnd(match.is_open_end || false);
      setIsPractice(match.is_practice_match || false);

      form.setFieldsValue({
        player1: match.player1,
        player2: match.player2,
        game_type_id: match.game_type_id,
        race_to: match.race_to || 1,
        best_of: match.best_of || 1,
        date: match.date ? dayjs(match.date) : null,
        is_open_end: match.is_open_end || false,
        is_practice_match: match.is_practice_match || false,
      });
    }
  }, [match, visible, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onUpdateMatch(match.id, values);
      onClose();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Modal
      title="Match bearbeiten"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Abbrechen
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Speichern
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          race_to: 1,
          best_of: 1,
          is_open_end: false,
          is_practice_match: false,
        }}
      >
        {/* Spieler 1 und 2 nebeneinander auf Mobile */}
        <Row gutter={[8, 0]}>
          <Col xs={12} sm={12} md={24}>
            {/* Spieler 1 */}
            <Form.Item
              name="player1"
              label="Spieler 1"
              rules={[{ required: true, message: "Bitte Spieler 1 auswählen!" }]}
            >
              <Select
                placeholder="Spieler auswählen"
                onChange={(value) => {
                  // Wenn Übungsspiel aktiv ist, automatisch Spieler 2 auf gleichen Wert setzen
                  if (isPractice) {
                    form.setFieldsValue({ player2: value });
                  }
                }}
              >
                {players.map((player) => (
                  <Select.Option key={player.id} value={player.id}>
                    {player.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={12} sm={12} md={24}>
            {/* Spieler 2 */}
            <Form.Item
              name="player2"
              label="Spieler 2"
              rules={[{ required: !isPractice, message: "Bitte Spieler 2 auswählen!" }]}
            >
              <Select placeholder="Spieler auswählen" disabled={isPractice}>
                {players.map((player) => (
                  <Select.Option key={player.id} value={player.id}>
                    {player.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Spielmodus */}
        <Form.Item
          name="game_type_id"
          label="Spielmodus"
          rules={[{ required: true, message: "Bitte Spielmodus auswählen!" }]}
        >
          <Select placeholder="Spielmodus auswählen">
            {gameTypes.map((type) => (
              <Select.Option key={type.id} value={type.id}>
                {type.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Übungsspiel und Open End Checkboxen nebeneinander auf Mobile */}
        <Row gutter={[8, 0]}>
          <Col xs={24} sm={12} md={24}>
            {/* Übungsspiel Checkbox */}
            <Form.Item name="is_practice_match" valuePropName="checked">
              <Checkbox
                onChange={(e) => {
                  setIsPractice(e.target.checked);
                  if (e.target.checked) {
                    // Bei Übungsspiel: Spieler 2 = Spieler 1
                    const player1 = form.getFieldValue("player1");
                    form.setFieldsValue({ player2: player1 });
                  }
                }}
              >
                Übungsspiel (gegen sich selbst)
              </Checkbox>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={24}>
            {/* Open End Checkbox */}
            <Form.Item name="is_open_end" valuePropName="checked">
              <Checkbox
                onChange={(e) => {
                  setIsOpenEnd(e.target.checked);
                  if (e.target.checked) {
                    // Bei Open End: race_to/best_of zurücksetzen
                    form.setFieldsValue({ race_to: null, best_of: null });
                  }
                }}
              >
                Open End (unbegrenzte Frames)
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        {/* Race-to / Best-of nur anzeigen wenn nicht Open End */}
        {!isOpenEnd && (
          <>
            <Form.Item label="Modus">
              <Radio.Group
                onChange={(e) => setRaceType(e.target.value)}
                value={raceType}
              >
                <Radio value="race_to">Race-to</Radio>
                <Radio value="best_of">Best-of</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              name={raceType}
              label={raceType === "race_to" ? "Race-to" : "Best-of"}
              rules={[{ required: !isOpenEnd, message: "Bitte eine Zahl eingeben!" }]}
            >
              <InputNumber min={1} step={2} placeholder="Zahl eingeben" />
            </Form.Item>
          </>
        )}

        {/* Datum und Uhrzeit */}
        <Form.Item
          name="date"
          label="Datum und Uhrzeit"
          rules={[{ required: true, message: "Bitte Datum und Uhrzeit auswählen!" }]}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder="Datum und Uhrzeit auswählen"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditMatchModal;
